import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ReactElement,
  forwardRef,
  useId,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import cx from "classnames";
import { Label } from "../Label";
import styles from "./Textarea.module.scss";
import { Helper, HelperProps } from "../../internal/components";
import { LayoutUtilProps, FieldLabelProps } from "../../types";
import {
  useLayoutPropsUtil,
  useMergeRefs,
  useOptionallyControlledState,
} from "../../hooks";

import TextareaAutosize, {
  type TextareaAutosizeProps,
} from "react-textarea-autosize";

/**
 * State object passed to onChange callback
 */
export type TextareaState = {
  /**
   * Current value of the textarea
   */
  value?: string | number | readonly string[];
};

/**
 * Props for the Textarea component
 * @extends Omit<ComponentPropsWithoutRef<"textarea">, "onChange" | "required">
 * @extends LayoutUtilProps
 * @extends FieldLabelProps
 */
export type TextareaProps = Omit<
  ComponentPropsWithoutRef<"textarea">,
  "onChange" | "required"
> &
  LayoutUtilProps &
  FieldLabelProps & {
    /**
     * Sets error style on textarea.
     * If string or ReactElement provided, places it under the label and sets error style.
     * Note: error string replaces helpText over, if both present.
     */
    error?: HelperProps["errorMessage"] | boolean;
    /**
     * Hint text displayed below the textarea
     */
    hint?: ReactElement | string;
    /**
     * Description text displayed below the textarea
     */
    description?: ReactElement | string;

    /**
     * Label for textarea is required
     * @accessibility This should either be a string or have text content inside for accessibility
     */
    label?: string | ReactElement;

    /**
     * Sets textarea's element no user-controllable method for resizing
     * @default false
     */
    disableResize?: boolean;

    /**
     * Fires on each change of textarea's value
     * @param e The change event
     * @param state Object containing the current value
     */
    onChange?: (
      e?: ChangeEvent<HTMLTextAreaElement>,
      state?: TextareaState,
    ) => void;
  } & TextareaConditionalProps &
  TextareaAutosizeConditionalProps &
  (
    | {
        /**
         * ARIA live region setting for error announcements
         */
        errorAriaLive?: HelperProps["errorAriaLive"];
      }
    | {
        /**
         * ARIA live region setting for error announcements
         * @deprecated Boolean value is no longer supported. Use `"off" | "assertive" | "polite"` instead
         */
        errorAriaLive?: boolean;
      }
  );

/**
 * Conditional props for character counter functionality
 */
type TextareaConditionalProps =
  | {
      /**
       * Show character counter
       */
      showCounter: true;
      /**
       * Maximum number of characters allowed
       */
      maxLength: HelperProps["maxLength"];
    }
  | {
      /**
       * Show character counter
       * @default false
       */
      showCounter?: false;
      /**
       * Maximum number of characters allowed
       */
      maxLength?: HelperProps["maxLength"];
    };

/**
 * Conditional props for auto-height functionality
 */
type TextareaAutosizeConditionalProps =
  | {
      /**
       * Auto height mode on typing
       */
      autoHeight: true;
      /**
       * Maximum number of rows when auto-height is enabled
       */
      maxRows?: TextareaAutosizeProps["maxRows"];
      /**
       * Minimum number of rows when auto-height is enabled
       */
      minRows?: TextareaAutosizeProps["minRows"];
    }
  | {
      /**
       * Auto height mode on typing
       * @default false
       */
      autoHeight?: false;
      maxRows?: never;
      minRows?: never;
    };

/**
 * Textarea component for multi-line text input with advanced features.
 *
 * Features:
 * - Character counter with maxLength validation
 * - Auto-height adjustment based on content
 * - Error state handling with custom error messages
 * - Hint and description text support
 * - Disable resize functionality
 * - Accessible with proper ARIA attributes
 * - Label association and required field support
 * - Focus and blur event handling
 * - Supports layout utilities for positioning and spacing
 * - SSR-safe auto-height implementation
 *
 * @example
 * <Textarea
 *   label="Description"
 *   placeholder="Enter your description here"
 *   onChange={(e, state) => console.log('Value:', state?.value)}
 * />
 *
 * @example
 * <Textarea
 *   label="Bio"
 *   showCounter
 *   maxLength={500}
 *   hint="Tell us about yourself"
 * />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      value: valueProp,
      defaultValue: defaultValueProp,
      className,
      disableResize,
      error,
      errorAriaLive: errorAriaLiveProp = "assertive",
      hint,
      description,
      label,
      moreInfo,
      onChange,
      required,
      showCounter,
      onFocus,
      onBlur,
      id: idProp,
      maxLength,
      autoHeight,
      labelProps,
      placeholder,
      style,
      ...rest
    } = componentProps;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isTyping, setIsTyping] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [errorAriaLive, setErrorAriaLive] =
      useState<HelperProps["errorAriaLive"]>("off");
    const [ssr, setSSR] = useState(true);

    // Use the optionally controlled state for value
    const [value, setValue] = useOptionallyControlledState<typeof valueProp>({
      controlledValue: valueProp,
      defaultValue: defaultValueProp,
    });

    const length = useMemo(() => {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) {
        return String(value).length;
      }
      return 0;
    }, [value]);

    useEffect(() => {
      setSSR(false);
    }, []);

    const onChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      setValue(e.target.value);
      onChange?.(e, { value: e.target.value });
      if (!isTyping) {
        setIsTyping(true);
      }
    };

    const focusHandler: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      onFocus?.(e);
      setOpenInfo(true);
      // type assertion is needed because of the deprecated boolean type
      setErrorAriaLive(errorAriaLiveProp as HelperProps["errorAriaLive"]);
    };

    const blurHandler: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      onBlur?.(e);
      setOpenInfo(false);
      setErrorAriaLive("off");
      if (isTyping) {
        setIsTyping(false);
      }
    };

    const labelUid = useId();
    const id = idProp ?? labelUid;

    const helperUid = useId();

    const errorMessage = typeof error !== "boolean" ? error : undefined;

    const textareaClassNames = cx([styles["textarea"]], className, {
      [styles["error"]]: error,
      [styles["disable-resize"]]: disableResize,
      [styles["disabled"]]: rest.disabled,
      [styles["readonly"]]: rest.readOnly,
      [styles["has-cols"]]: rest.cols != null,
    });

    const styleCombined = { ...style, ...layoutStyles };

    const TextareaComponent =
      autoHeight && !ssr ? TextareaAutosize : "textarea";

    return (
      <div
        className={textareaClassNames}
        data-anv="textarea"
        style={styleCombined}
      >
        {label && (
          <Label
            htmlFor={id}
            moreInfo={moreInfo}
            openMoreInfo={openInfo}
            required={required}
            {...labelProps}
          >
            {label}
          </Label>
        )}

        <TextareaComponent
          rows={3}
          onChange={onChangeHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
          ref={useMergeRefs([ref, textareaRef])}
          required={required}
          maxLength={maxLength}
          id={id}
          placeholder={placeholder}
          value={value ?? ""}
          {...rest}
          aria-label={label ? undefined : placeholder}
          aria-describedby={helperUid}
          aria-invalid={error ? !!error : undefined}
        />
        {hint || showCounter || errorMessage || description ? (
          <Helper
            id={helperUid}
            hint={hint}
            maxLength={maxLength}
            inputLength={length}
            isTyping={isTyping}
            showCounter={showCounter}
            errorMessage={errorMessage}
            errorAriaLive={errorAriaLive}
            description={description}
          />
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";
